import { isSiteLanguageCode, normalizeSiteLanguage, type SiteLanguageCode } from '@yoyoclub/shared';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { authApi } from '../services/api';

/**
 * The language switcher is the user's language preference (PRD §5.6). While
 * signed in it is saved to the account: at sign-in the account's choice wins
 * (or the current choice is saved when the account has none), and every later
 * switch is saved. Saving is best effort and never blocks the switch.
 *
 * Saves run one at a time and each sends the latest choice when its turn
 * comes, so after rapid switches the account always ends on the last one.
 */
export function useLanguagePreferenceSync(userId: string | null): void {
  const signedIn = userId !== null;
  const { i18n } = useTranslation();
  // Read through a ref: the sync must re-run only when the account changes, never
  // on a language switch (re-reading the account then could undo the switch).
  const i18nRef = useRef(i18n);
  i18nRef.current = i18n;
  // True while applying the account's choice, so it is not saved straight back.
  const applying = useRef(false);
  // Switches are saved only after the sign-in read settles, so a quick switch
  // cannot be overwritten by an older account value.
  const ready = useRef(false);
  const queue = useRef<Promise<void>>(Promise.resolve());
  // Bumped on every account change; queued saves from an earlier account are dropped.
  const generation = useRef(0);
  const wanted = useRef<SiteLanguageCode | null>(null);
  const saved = useRef<SiteLanguageCode | null>(null);

  const save = (language: SiteLanguageCode) => {
    const gen = generation.current;
    wanted.current = language;
    queue.current = queue.current.then(async () => {
      if (gen !== generation.current) return;
      const target = wanted.current;
      if (!target || target === saved.current) return;
      try {
        await authApi.updateProfile({ preferredLanguage: target });
        if (gen === generation.current) saved.current = target;
      } catch {
        // Kept locally; the next switch tries again.
      }
    });
  };

  useEffect(() => {
    generation.current += 1;
    ready.current = false;
    saved.current = null;
    wanted.current = null;
    queue.current = Promise.resolve();
    if (!signedIn) return;
    const i18n = i18nRef.current;
    let cancelled = false;
    const sync = async () => {
      try {
        const { preferredLanguage } = await authApi.getPreferences();
        if (cancelled) return;
        const current = normalizeSiteLanguage(i18n.language);
        if (isSiteLanguageCode(preferredLanguage)) {
          saved.current = preferredLanguage;
          if (preferredLanguage !== current) {
            applying.current = true;
            try {
              await i18n.changeLanguage(preferredLanguage);
            } finally {
              applying.current = false;
            }
          }
        } else {
          save(current);
        }
      } catch {
        // Offline or not provisioned yet: keep the local choice.
      } finally {
        if (!cancelled) ready.current = true;
      }
    };
    void sync();
    return () => {
      cancelled = true;
    };
    // save and i18nRef are refs; only an account change restarts the sync
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!signedIn) return;
    const i18n = i18nRef.current;
    const onSwitch = (lng: string) => {
      if (applying.current || !ready.current) return;
      save(normalizeSiteLanguage(lng));
    };
    i18n.on('languageChanged', onSwitch);
    return () => i18n.off('languageChanged', onSwitch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
}
